<template>
  <teleport to="body">
    <transition :name="instant ? 'tooltip-instant' : 'tooltip-fade'" appear>
      <div
        v-if="visible && !loading"
        ref="popoverRef"
        :class="$style.popover"
        :style="popoverStyle"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <a
          :class="[$style.avatarAndTitle, { [$style.withBorder]: content?.items?.length }]"
          :href="props.href || undefined"
          role="link"
          @click.prevent="handleNavigate"
          @mouseenter="handleAvatarMouseEnter"
          @mouseleave="handleAvatarMouseLeave"
        >
          <template v-if="shouldShowSpecAvatar">
            <SCASvgIcon
              v-if="content.specialIcon"
              :class="$style.specAvatarIcon"
              :icon="content.specialIcon"
              size="80px"
            />
          </template>
          <template v-else>
            <SCAAvatar
              :initials="convertNameToInitials(getTitle())"
              :color-idx="getColorIdx()"
              shape="rounded"
              size="80"
              :bordered="!!getAvatarUrl()"
              :hovered="avatarHovered"
            >
              <img v-if="getAvatarUrl()" :src="getAvatarUrl()" :alt="getTitle()" loading="lazy" />
              <SCASvgIcon v-else-if="!getTitle() && defaultModuleIcon" :icon="defaultModuleIcon" size="36px" />
            </SCAAvatar>
          </template>
          <div :class="$style.title">
            {{ getTitle() }}
          </div>
        </a>
        <div v-if="content.items.length" :class="$style.content">
          <div
            v-for="(item, index) in content.items"
            :key="index"
            :class="[$style.contentItem, { [$style.interactive]: isInteractive(item) }]"
          >
            <div :class="$style.itemIconContainer">
              <SCASvgIcon :class="$style.itemIcon" :icon="getIconByType(item.type)"></SCASvgIcon>
            </div>
            <component
              :is="getItemRenderer(item)"
              :item="item"
              :class="[
                $style.itemText,
                {
                  [$style.nowrap]:
                    item.type === DetailsTooltipContentItemType.EMAIL ||
                    item.type === DetailsTooltipContentItemType.PHONE,
                  [$style.hasAction]: isInteractive(item),
                },
              ]"
              @click="handleItemClick($event, item)"
            />
          </div>
        </div>
        <div ref="arrowRef" :class="$style.arrow" :style="arrowStyle"></div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import type { Placement, VirtualElement } from "@floating-ui/vue";
import { arrow, autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/vue";
import { convertNameToInitials } from "@scayla/core";
import type { Component, CSSProperties } from "vue";
import { computed, h, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import SCAAvatar from "../SCAAvatar.vue";
import SCASvgIcon from "../SCASvgIcon.vue";
import type { DetailsTooltipContent, DetailsTooltipContentItem } from "./tooltipTypes";
import { DetailsTooltipContentItemType } from "./tooltipTypes";

const props = defineProps<{
  visible: boolean;
  content: DetailsTooltipContent;
  targetRect: DOMRect | null;
  placement: string;
  loading: boolean;
  avatarType?: "initials" | "special";
  instant?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onNavigate?: () => Promise<void> | void;
  href?: string;
}>();

const emit = defineEmits<{
  mouseEnter: [];
  mouseLeave: [];
}>();

const avatarHovered = ref(false);
const shouldShowSpecAvatar = computed(() => {
  return (props.avatarType ?? "initials") === "special" && !getAvatarUrl();
});

const handleAvatarMouseEnter = () => {
  avatarHovered.value = true;
};

const handleAvatarMouseLeave = () => {
  avatarHovered.value = false;
};

const getIconByType = (type: DetailsTooltipContentItemType) => {
  switch (type) {
    case DetailsTooltipContentItemType.EMAIL:
      return "phosphor/envelope";
    case DetailsTooltipContentItemType.PHONE:
      return "phosphor/phone";
    case DetailsTooltipContentItemType.ADDRESS:
      return "phosphor/map-pin-simple-area";
    case DetailsTooltipContentItemType.TEMPLATE:
      return "phosphor/notebook";
    case DetailsTooltipContentItemType.PARENT:
      return "phosphor/tree-view";
    default:
      return "phosphor/clipboard-text";
  }
};

const getItemRenderer = (item: DetailsTooltipContentItem): Component => {
  switch (item.type) {
    case DetailsTooltipContentItemType.EMAIL:
      return {
        render: () => h("a", { href: item.href ?? `mailto:${item.text}` }, item.text),
      };
    case DetailsTooltipContentItemType.PHONE:
      return {
        render: () => h("a", { href: `tel:${item.text}` }, item.text),
      };
    case DetailsTooltipContentItemType.ADDRESS:
      return {
        render: () => {
          const parts = Array.isArray(item.text) ? item.text.filter(Boolean) : [item.text];
          const query = parts.join(", ");

          return h(
            "a",
            {
              href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
              target: "_blank",
              rel: "noopener noreferrer",
            },
            parts.map((part, i) => (i < parts.length - 1 ? [part, h("br")] : part)),
          );
        },
      };

    default:
      return {
        render: () => h("div", item.text),
      };
  }
};

const getTitle = () => {
  return props.loading ? "" : props.content.title;
};

const getAvatarUrl = () => {
  return props.loading ? undefined : props.content.avatarUrl;
};

const getColorIdx = () => {
  return props.loading ? undefined : props.content.avatarColorIdx;
};

const defaultModuleIcon = computed(() => (props.loading ? undefined : props.content.defaultModuleIcon));

const handleNavigate = async () => {
  await props.onNavigate?.();
};

// Modifier/middle clicks fall through to the item's real href so ctrl/cmd-click-to-open-in-new-tab
// still works, matching LabeledValueListField.vue's onLinkClick.
const handleItemClick = (event: MouseEvent, item: DetailsTooltipContentItem) => {
  event.stopPropagation();
  if (!item.action) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return;
  event.preventDefault();
  item.action();
};

const popoverRef = ref<HTMLElement | null>(null);
const arrowRef = ref<HTMLElement | null>(null);

const popoverStyle = ref<CSSProperties>({ position: "absolute", top: "0", left: "0" });
const arrowStyle = ref<CSSProperties>({ position: "absolute", top: "0", left: "0" });

const virtualEl = ref<VirtualElement | null>(null);

let cleanup: (() => void) | null = null;

const syncUpdate = () => {
  void updatePosition();
};

const updatePosition = async () => {
  if (!popoverRef.value || !virtualEl.value) return;

  const { x, y, middlewareData, placement } = await computePosition(virtualEl.value, popoverRef.value, {
    placement: props.placement as Placement,
    middleware: [
      offset({ mainAxis: 14, crossAxis: -20 }),
      flip(),
      shift({ padding: 0 }),
      arrow({ element: arrowRef.value! }),
    ],
  });

  popoverStyle.value = {
    position: "absolute",
    top: `${y}px`,
    left: `${x}px`,
  };

  const arrowData = middlewareData.arrow;
  if (arrowData) {
    const { x: arrowX, y: arrowY } = arrowData;

    const basePlacement = placement.split("-")[0] as "top" | "bottom" | "left" | "right";

    const arrowSide = getArrowSide(basePlacement);
    const rotation = getArrowRotation(basePlacement);

    arrowStyle.value = {
      position: "absolute",
      left: arrowX != null ? `${arrowX}px` : "",
      top: arrowY != null ? `${arrowY}px` : "",
      [arrowSide]: "-8px",
      transform: `rotate(${rotation}deg)`,
    };
  }
};

const getArrowSide = (placement: Placement) => {
  const basePlacement = placement.split("-")[0] as "top" | "bottom" | "left" | "right";
  switch (basePlacement) {
    case "top":
      return "bottom";
    case "bottom":
      return "top";
    case "left":
      return "right";
    case "right":
      return "left";
    default:
      return "bottom";
  }
};

const getArrowRotation = (placement: "top" | "bottom" | "left" | "right") => {
  switch (placement) {
    case "top":
      return 0;
    case "bottom":
      return 180;
    case "left":
      return 90;
    case "right":
      return -90;
    default:
      return 0;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      if (props.targetRect) {
        virtualEl.value = {
          getBoundingClientRect: () => props.targetRect!,
        };
      }
      await updatePosition();
      if (popoverRef.value && virtualEl.value) {
        await nextTick();
        cleanup = autoUpdate(virtualEl.value, popoverRef.value, syncUpdate);
      }
      document.addEventListener("click", handleClickOutside);
    } else {
      virtualEl.value = null;
      if (cleanup) cleanup();
      document.removeEventListener("click", handleClickOutside);
    }
  },
);

const handleMouseEnter = () => {
  props.onMouseEnter();
  emit("mouseEnter");
};

const handleMouseLeave = () => {
  props.onMouseLeave();
  emit("mouseLeave");
};

const handleClickOutside = (event: MouseEvent) => {
  if (popoverRef.value && !popoverRef.value.contains(event.target as Node)) {
    props.onMouseLeave();
  }
};

const isInteractive = (item: DetailsTooltipContentItem) => {
  return (
    [
      DetailsTooltipContentItemType.EMAIL,
      DetailsTooltipContentItemType.PHONE,
      DetailsTooltipContentItemType.ADDRESS,
    ].includes(item.type) || !!item.action
  );
};

onMounted(async () => {
  if (props.visible && props.targetRect) {
    virtualEl.value = {
      getBoundingClientRect: () => props.targetRect!,
    };
    await updatePosition();
    if (popoverRef.value && virtualEl.value) {
      await nextTick();
      cleanup = autoUpdate(virtualEl.value, popoverRef.value, syncUpdate);
    }
    document.addEventListener("click", handleClickOutside);
  }
});

onUnmounted(() => {
  virtualEl.value = null;
  if (cleanup) cleanup();
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../../styles/icon.scss" as IconMixins;

.popover {
  max-width: 312px;
  min-width: 200px;
  max-height: 312px;
  background: var(--color-background-default);
  border-radius: var(--border-radius-large);
  z-index: 1000;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-shadow: 0px -2px 19.5px 0px #0000004a;
  position: relative;
  display: flex;
  flex-direction: column;
}

.arrow {
  width: 16px;
  height: 8px;
  background: var(--color-background-default);
  clip-path: polygon(0% 0%, 100% 0%, 50% 100%);
  transform: rotate(0deg);
  z-index: 1001;
}

.avatarAndTitle {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--space-component-padding-medium);
  gap: var(--space-component-gap-small);
  flex-shrink: 0;
  &:hover {
    color: var(--color-interaction-default-active);
  }
}

.specAvatarIcon {
  @include IconMixins.icon-color(var(--color-blue-0));
}

.withBorder {
  border-bottom: 1px solid var(--color-border-default);
}

.title {
  @include FigmaTypoMixins.typo-body-large;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-small);
  padding: var(--space-component-padding-medium) var(--space-component-padding-medium) 0;
  margin-bottom: var(--space-component-padding-medium);
  flex: 1;
  overflow-y: auto;
}

.contentItem {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--space-component-gap-small);
}

.itemIconContainer {
  width: var(--dimension-300);
  height: var(--dimension-300);
  display: flex;
  justify-content: center;
  align-items: center;
}

.itemIcon {
  @include IconMixins.icon-color(var(--color-interaction-disabled-normal));
  flex-shrink: 0;
}

.interactive {
  cursor: pointer;
}

.interactive:hover .itemText {
  color: var(--color-interaction-default-active);
}

.interactive:hover .itemIcon {
  @include IconMixins.icon-color(var(--color-interaction-default-active));
}

.itemText {
  @include FigmaTypoMixins.typo-body-small;
  line-height: 24px;
  color: var(--color-content-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  cursor: default;

  &:is(div[innerHTML]) {
    white-space: pre-line;
  }

  &.hasAction {
    cursor: pointer;
    color: var(--color-interaction-primary-normal);
    text-decoration: none;
  }
}

.nowrap {
  white-space: nowrap;
}

/* Transitions */
:global(.tooltip-fade-enter-active),
:global(.tooltip-fade-leave-active) {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

:global(.tooltip-fade-enter-from),
:global(.tooltip-fade-leave-to) {
  opacity: 0;
  transform: scale(0.95);
}

/* Route changes hide the tooltip without a fade — its anchor is already gone. */
:global(.tooltip-instant-leave-active) {
  transition: none;
}

:global(.tooltip-instant-leave-to) {
  opacity: 0;
}
</style>
